import ExcelJS from 'exceljs';
import { DeviceIsSystemType } from 'features/integrations/utils/deviceComparisionUtils';
import { awareFloodSensorData } from 'pages/System/GlobalActions/Devices/DeviceKeyMaps';
import { useState } from 'react';
import { CUSTOM_URL, TEAM_PREFIX } from 'services/_infra/environment';
import { useGetConnectedDevicesQuery } from 'services/devices/devices.api';
import { getNodeDataByTimestamps } from 'services/devices/devices.service';

const ExportModal = ({ startTime, setStartTime, endTime, setEndTime, formatDateTimeLocal }) => {
  const [exportFormat, setExportFormat] = useState('csv');
  const [selectedDevices, setSelectedDevices] = useState([]);
  const {
    data: connectedDevices = [],
    isLoading: connectedDevicesLoading,
    isError: connectedDevicesError,
  } = useGetConnectedDevicesQuery();

  const BASE_FIELDS = ['NI', 'ts', 'pict', 'img_link_url'];

  const EXCLUDED_FIELDS = ['data_hash', 'img_sys_path', 'hash_counter', 'id'];

  const [selectedFields, setSelectedFields] = useState(BASE_FIELDS);
  const HEADER_MAP = awareFloodSensorData;
  const [timeframeData, setTimeframeData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [customTimeInputs, setCustomTimeImputs] = useState({});
  const [exportingString, setExportingString] = useState('');
  const [includeImages, setIncludeImages] = useState(false);

  const handleExport = () => {
    if (selectedDevices.length === 0) {
      alert('Please select at least one device to export.');
      return;
    }

    setExportingString('Exporting...');
    //get new dataset from server
    getNodeDataByTimestamps(startTime, endTime, selectedDevices, true).then((res) => {
      const resData = res.data.result;
      if (resData.length === 0) {
        setExportingString('');
        alert('No data available for the selected timeframe.');
        return;
      }

      setTimeframeData(resData);
      //filter data

      //add base fields to selected fields
      let selectedFieldsFull = [...selectedFields, ...BASE_FIELDS];
      //remove excluded fields from selected fields
      selectedFieldsFull = selectedFieldsFull.filter((field) => {
        return !EXCLUDED_FIELDS.includes(field);
      });

      const filteredData = resData.map((device) => {
        const filteredDeviceData = device.map((deviceData) => {
          const filteredData = {};
          Object.keys(deviceData).forEach((field) => {
            if (selectedFieldsFull.includes(field)) {
              filteredData[field] = deviceData[field];
            }
          });
          return filteredData;
        });
        return {
          node_id: device.node_id,
          data: filteredDeviceData,
        };
      });
      setFilteredData(filteredData);
      if (filteredData.length === 0) {
        setExportingString('');
        alert('No filtered data available for the selected devices.');
        return;
      }

      const mappedData = filteredData.map((device) => {
        return device.data.map((deviceData) => {
          const mappedData = {};
          Object.keys(deviceData).forEach((field) => {
            if (field === 'ts') {
              mappedData[field] = new Date(deviceData[field]).toLocaleString();
            } else if (field === 'gatewayType') {
              mappedData[field] = device[field] === 1 ? 'FirstNet Cellular' : 'Satellite';
            } else if (field === 'calType') {
              const calibrationType = [
                'Lower Pressure Sensor',
                'Lower Pressure Sensor + SDI-12 Soil Moisture',
                'Lower Pressure Sensor + Waterlog Rain Sensor',
                'Lower Pressure Sensor + Intellisense Rain Sensor',
                'Water Level Radar',
              ];
              mappedData[field] = calibrationType[deviceData[field]];
            } else {
              mappedData[field] = deviceData[field];
            }
          });
          return mappedData;
        });
      });

      if (!mappedData || mappedData.length === 0) {
        setExportingString('');
        alert('No matching data available for the selected devices.');
        return;
      }
      //export data
      if (exportFormat === 'csv') {
        const headers = Object.keys(mappedData[0][0])
          .map((header) => `"${HEADER_MAP[header] || header}"`)
          .join(',');
        const csvData = mappedData
          .map((device) => {
            return device
              .map((device) => {
                return Object.values(device)
                  .map((value) => `"${String(value).replace(/"/g, '""')}"`)
                  .join(',');
              })
              .join('\n');
          })
          .join('\n');
        const csvString = headers + '\n' + csvData;

        const csvBlob = new Blob([csvString], { type: 'text/csv' });
        const csvUrl = URL.createObjectURL(csvBlob);
        const a = document.createElement('a');
        a.href = csvUrl;
        a.download = 'export.csv';
        a.click();

        setExportingString('Download complete.');
        setTimeout(() => {
          setExportingString('');
        }, 5000);
      } else if (exportFormat === 'xls') {
        let headers = Object.keys(mappedData[0][0]);
        if (includeImages) headers.push('image'); // Add 'image' column to headers
        // headers = headers.filter((header) => header !== "img_link_url"); // Remove 'pict' column from headers
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Sheet1');

        // Add headers
        worksheet.addRow(headers.map((header) => HEADER_MAP[header] || header));

        // Add data
        mappedData.forEach((device) => {
          device.forEach((deviceData) => {
            const row = headers.map((header) => {
              if (header === 'image') {
                return null;
              } else {
                return deviceData[header];
              }
            });
            worksheet.addRow(row);
          });
        });
        worksheet.eachRow((row, rowNumber) => {
          if (includeImages && rowNumber !== 1) row.height = 160;
          row.eachCell((cell) => {
            cell.alignment = { vertical: 'top', wrapText: true };
          });
        });

        worksheet.columns.forEach((column, index) => {
          let maxLength = 0;
          column.eachCell({ includeEmpty: true }, (cell) => {
            const columnLength = cell.value ? cell.value.toString().length : 10;
            if (columnLength > maxLength) {
              maxLength = columnLength;
            }
          });
          column.width = maxLength + 2; // Add some padding to the width
        });
        // Add images
        const initTime = new Date().getTime();
        const addImages = async () => {
          if (!includeImages) {
            return;
          }

          let rowIndex = 1;

          const fetchPromises = [];
          const uniquePicts = new Set();
          const imageFetchMap = new Map();

          for (let device of mappedData) {
            for (let [i, deviceData] of device.entries()) {
              const imageUrl = deviceData['img_link_url'];

              if (!imageUrl) {
                continue;
              }

              if (uniquePicts.has(imageUrl)) {
                fetchPromises.push(imageFetchMap.get(imageUrl));
                continue;
              }

              uniquePicts.add(imageUrl);

              const localImageUrl = `${CUSTOM_URL}${TEAM_PREFIX}/fusion/serve-image?url=${encodeURIComponent(
                // hfjdkshaflkdsa
                imageUrl,
              )}`;

              const fetchPromise = fetch(localImageUrl)
                .then((response) => {
                  if (!response.ok) {
                    console.error(
                      `Fetch failed for URL: ${localImageUrl} with status: ${response.status}`,
                    );
                    throw new Error(`HTTP error! status: ${response.status}`);
                  }

                  setExportingString(
                    `Adding images: ${i + 1}/${device.length * mappedData.length}`,
                  );
                  return response.arrayBuffer();
                })
                .catch((error) => {
                  console.error(`Error fetching image via backend: ${localImageUrl}, error`);
                  throw error;
                });

              imageFetchMap.set(imageUrl, fetchPromise);
              fetchPromises.push(fetchPromise);
            }
          }

          const imageBuffers = await Promise.all(fetchPromises);

          setExportingString('Building file...');
          let bufferIndex = 0;
          for (let device of mappedData) {
            for (let [i, deviceData] of device.entries()) {
              const imageUrl = deviceData['img_link_url'];

              if (!imageUrl) {
                continue;
              }

              const imageBuffer = imageBuffers[bufferIndex++];
              const imageId = workbook.addImage({
                buffer: imageBuffer,
                extension: 'jpeg',
              });
              worksheet.addImage(imageId, {
                tl: { col: headers.indexOf('image'), row: rowIndex++ },
                ext: { width: 64 * 4, height: 48 * 4 },
              });
            }
          }
          setExportingString('Downloading...');
        };

        addImages()
          .then(() => {
            // Generate Excel file and trigger download
            const workbookStartTime = new Date().getTime();
            workbook.xlsx
              .writeBuffer()
              .then((buffer) => {
                const workbookEndTime = new Date().getTime();
                const blob = new Blob([buffer], {
                  type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'export.xlsx';
                a.click();
                setExportingString('Download complete.');
                const currentTime3 = new Date().getTime();
                setTimeout(() => {
                  setExportingString('');
                }, 5000);
              })
              .catch((err) => {
                console.error(err);
                setExportingString('Download failed.');
              });
          })
          .catch((err) => {
            console.error(err);
            setExportingString('Failed to add images.');
          });
      }
    });
  };

  return (
    <div className="d-flex flex-column gap-2">
      <div className="d-flex flex-column flex-lg-row gap-2">
        <div className="d-flex flex-column flex-fill flex-basis-0">
          <div className="d-flex flex-column flex-lg-row align-items-baseline">
            <label className="flex-fill flex-basis-0">Start Date</label>
            <input
              className="form-control flex-fill flex-basis-0"
              type="datetime-local"
              value={customTimeInputs.start || formatDateTimeLocal(startTime)}
              onChange={(e) => {
                setCustomTimeImputs({
                  ...customTimeInputs,
                  start: e.target.value,
                });
                setStartTime(new Date(e.target.value).getTime());
              }}
            />
          </div>
        </div>

        <div className="d-flex flex-column flex-fill flex-basis-0">
          <div className="d-flex flex-column flex-lg-row align-items-baseline">
            <label className="flex-fill flex-basis-0">End Date</label>
            <input
              className="form-control flex-fill flex-basis-0"
              type="datetime-local"
              value={customTimeInputs.end || formatDateTimeLocal(endTime)}
              onChange={(e) => {
                setCustomTimeImputs({
                  ...customTimeInputs,
                  end: e.target.value,
                });

                setEndTime(new Date(e.target.value).getTime());
              }}
            />
          </div>
        </div>
      </div>

      <hr className="d-block d-lg-none" />

      <div className="d-flex flex-column flex-lg-row gap-2">
        <div className="flex-fill flex-basis-0">
          <div>
            <p className="h5">Select Device(s)</p>
            <div className="d-flex gap-2">
              <input
                className="form-check-input"
                type="checkbox"
                checked={
                  connectedDevices.length > 0 &&
                  selectedDevices.length ===
                    connectedDevices.filter((device) => {
                      return DeviceIsSystemType(device, 'Flood') && parseInt(device.id);
                    }).length
                }
                onChange={(e) => {
                  if (e.target.checked) {
                    const allDeviceIds = connectedDevices
                      .map((device) => {
                        if (DeviceIsSystemType(device, 'Flood') && parseInt(device.id))
                          return parseInt(device.id);
                      })
                      .filter((deviceId) => deviceId !== undefined);
                    setSelectedDevices(allDeviceIds);
                  } else {
                    setSelectedDevices([]);
                  }
                }}
              />
              <label>Select All</label>
            </div>
          </div>

          <div className="position-relative min-h-250 bg-primary-shadow ">
            <div className="position-absolute-fill d-flex flex-column overflow-x-hidden overflow-y-auto gap-2 p-2">
              {connectedDevices
                .filter((device) => {
                  return DeviceIsSystemType(device, 'Flood');
                })
                .map((device) => {
                  return (
                    <div className="d-flex gap-2">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={selectedDevices.includes(parseInt(device.id))}
                        onChange={() => {
                          if (selectedDevices.includes(parseInt(device.id))) {
                            const newSelectedDevices = selectedDevices.filter(
                              (id) => id !== parseInt(device.id),
                            );
                            setSelectedDevices(newSelectedDevices);
                          } else {
                            setSelectedDevices([...selectedDevices, parseInt(device.id)]);
                          }
                        }}
                      />
                      <label>
                        {(device.device_type !== 'Manual Road Closure'
                          ? device.static_id + ' '
                          : '') + device.alias}
                      </label>
                    </div>
                  );
                })}
            </div>
          </div>
        </div>

        <div className="flex-fill flex-basis-0">
          <div>
            <p className="h5">Data to Export</p>
            <div className="d-flex gap-2">
              <input
                className="form-check-input"
                type="checkbox"
                checked={
                  selectedFields.length ===
                  Object.keys(connectedDevices[0].data[0]).length -
                    EXCLUDED_FIELDS.length -
                    BASE_FIELDS.length
                }
                onChange={(e) => {
                  if (e.target.checked) {
                    const allFields = Object.keys(connectedDevices[0].data[0]).filter((field) => {
                      if (BASE_FIELDS.includes(field)) {
                        return false;
                      } else if (EXCLUDED_FIELDS.includes(field)) {
                        return false;
                      } else {
                        return true;
                      }
                    });
                    setSelectedFields(allFields);
                  } else {
                    setSelectedFields(BASE_FIELDS);
                  }
                }}
              />
              <label>Select All</label>
            </div>
          </div>

          <div className="position-relative min-h-250 bg-primary-shadow ">
            <div className="position-absolute-fill d-flex flex-column overflow-x-hidden overflow-y-auto gap-2 p-2">
              {Object.keys(connectedDevices[0].data[0])
                .filter((field) => {
                  if (BASE_FIELDS.includes(field)) {
                    return false;
                  } else if (EXCLUDED_FIELDS.includes(field)) {
                    return false;
                  } else {
                    return true;
                  }
                })
                .sort((a, b) => {
                  return HEADER_MAP[a] > HEADER_MAP[b] ? 1 : -1;
                })
                .map((field) => {
                  return (
                    <div className="d-flex gap-2">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={selectedFields.includes(field)}
                        onChange={() => {
                          if (selectedFields.includes(field)) {
                            const newSelectedFields = selectedFields.filter((f) => f !== field);
                            setSelectedFields(newSelectedFields);
                          } else {
                            setSelectedFields([...selectedFields, field]);
                          }
                        }}
                      />
                      <label>{HEADER_MAP[field] || field}</label>
                    </div>
                  );
                })}
            </div>
          </div>
        </div>
      </div>

      <hr className="d-block d-lg-none" />

      <div className="d-flex flex-column flex-lg-row justify-content-between">
        <div>
          <div className="h5">Export Document Format</div>
          <div className="d-flex gap-4">
            <div>
              <input
                className="form-check-input"
                type="checkbox"
                checked={exportFormat === 'csv'}
                onChange={() => setExportFormat('csv')}
              />
              <label>CSV</label>
            </div>

            <div>
              <input
                className="form-check-input"
                type="checkbox"
                checked={exportFormat === 'xls'}
                onChange={() => setExportFormat('xls')}
              />
              <label>Excel</label>
            </div>
          </div>
        </div>

        <div className="d-flex gap-2 justify-content-between align-items-baseline">
          <div>
            {exportFormat === 'xls' && (
              <div className="d-flex">
                <input
                  className="form-check-input me-2"
                  type="checkbox"
                  checked={includeImages}
                  onChange={() => setIncludeImages(!includeImages)}
                />
                <label>Include Images (Larger file)</label>
              </div>
            )}
            {exportingString || ' '}
          </div>
          <button
            className="btn btn-primary"
            onClick={() => {
              handleExport();
            }}
          >
            <p className="m-0 fw-bold py-2">Export</p>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExportModal;
